import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  Upload, FileText, Clock, CheckCircle, AlertCircle, 
  Loader2, Sparkles, Eye, Truck, Users, Send, Building, MapPin, Calendar, X, Printer, Download
} from 'lucide-react';
import {
  extractMultiVehicleTimesheet, confirmOCRExtraction,
  getExtractionVehicles, mapVehicleToSupplier, createSupplierTimesheets,
  listSupplierTimesheets, sendSupplierTimesheet, listOCRExtractions,
  getCompanies, getSuppliers
} from '../lib/api';

export const SupplierTimesheetsPage = () => {
  const [activeTab, setActiveTab] = useState('upload');
  const [uploading, setUploading] = useState(false);
  const [extractions, setExtractions] = useState([]);
  const [supplierTimesheets, setSupplierTimesheets] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  
  // Current extraction being processed
  const [currentExtraction, setCurrentExtraction] = useState(null);
  const [extractionVehicles, setExtractionVehicles] = useState([]);
  const [vehicleSupplierMap, setVehicleSupplierMap] = useState({});
  const [showMappingDialog, setShowMappingDialog] = useState(false);
  const [processing, setProcessing] = useState(false);
  
  // Vehicle details dialog
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [showVehicleDialog, setShowVehicleDialog] = useState(false);

  // Helper function to normalize daily_hours data
  // Handles both formats: array of objects [{date, hours}] and object {1: 10, 2: 10}
  const normalizeDailyHours = useCallback((dailyHours) => {
    if (!dailyHours) return {};
    
    // If it's already an object with day keys, return as is
    if (!Array.isArray(dailyHours)) return dailyHours;
    
    // Convert array format to object format
    const normalized = {};
    dailyHours.forEach(entry => {
      if (entry.date) {
        const day = parseInt(entry.date.split('-')[2], 10); // Extract day from YYYY-MM-DD
        normalized[day] = entry.hours ?? entry.hours_worked ?? 0;
      }
    });
    return normalized;
  }, []);

  // Helper function to compute vehicle stats
  const computeVehicleStats = useCallback((vehicle) => {
    const rawDailyHours = vehicle?.daily_hours || {};
    const dailyHours = normalizeDailyHours(rawDailyHours);
    let workingDays = 0;
    let offDays = 0;
    let totalHours = 0;

    for (let i = 1; i <= 31; i++) {
      const key = String(i);
      const hours = dailyHours[key] ?? dailyHours[i];
      if (hours !== undefined && hours !== null && hours !== '-' && hours !== 'X' && hours !== 'F' && hours !== 'OFF' && Number(hours) > 0) {
        workingDays++;
        totalHours += Number(hours) || 0;
      } else if (hours !== undefined && hours !== null) {
        offDays++;
      }
    }

    const avgPerDay = workingDays > 0 ? (totalHours / workingDays).toFixed(1) : 0;
    return { workingDays, offDays, totalHours, avgPerDay };
  }, [normalizeDailyHours]);

  // Open vehicle details dialog
  const handleViewVehicleDetails = (vehicle) => {
    setSelectedVehicle(vehicle);
    setShowVehicleDialog(true);
  };

  // Print/Export vehicle timesheet as PDF
  const handlePrintVehicleTimesheet = useCallback((vehicle) => {
    if (!vehicle) return;
    
    const dailyHours = normalizeDailyHours(vehicle.daily_hours);
    const stats = computeVehicleStats(vehicle);
    const clientName = currentExtraction?.client_name || currentExtraction?.extracted_data?.client_name || 'Client';
    const periodMonth = currentExtraction?.extracted_data?.period_month || 'N/A';
    
    // Generate printable HTML
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Vehicle Timesheet - ${vehicle.vehicle_number}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: Arial, sans-serif; padding: 20px; color: #333; }
          .header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #2563eb; padding-bottom: 15px; }
          .header h1 { font-size: 24px; color: #1e40af; margin-bottom: 5px; }
          .header p { color: #666; font-size: 14px; }
          .vehicle-info { background: #eff6ff; padding: 15px; border-radius: 8px; margin-bottom: 20px; }
          .vehicle-info h2 { font-size: 18px; color: #1e40af; margin-bottom: 8px; }
          .vehicle-info p { font-size: 14px; color: #666; }
          .stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-bottom: 20px; }
          .stat-card { text-align: center; padding: 12px; border-radius: 8px; }
          .stat-card.working { background: #dcfce7; }
          .stat-card.off { background: #fee2e2; }
          .stat-card.total { background: #dbeafe; }
          .stat-card.avg { background: #f3e8ff; }
          .stat-card .value { font-size: 24px; font-weight: bold; }
          .stat-card.working .value { color: #16a34a; }
          .stat-card.off .value { color: #dc2626; }
          .stat-card.total .value { color: #2563eb; }
          .stat-card.avg .value { color: #9333ea; }
          .stat-card .label { font-size: 12px; color: #666; }
          .hours-table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
          .hours-table th, .hours-table td { border: 1px solid #e5e7eb; padding: 8px; text-align: center; font-size: 12px; }
          .hours-table th { background: #f3f4f6; font-weight: 600; }
          .hours-table .working { background: #dcfce7; color: #16a34a; font-weight: 600; }
          .hours-table .off { background: #fee2e2; color: #dc2626; }
          .hours-table .total { background: #dbeafe; color: #1e40af; font-weight: bold; }
          .footer { text-align: center; font-size: 11px; color: #999; margin-top: 20px; padding-top: 10px; border-top: 1px solid #e5e7eb; }
          @media print { 
            body { padding: 10px; } 
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Vehicle Timesheet</h1>
          <p>Day-Wise Hours Breakdown</p>
        </div>
        
        <div class="vehicle-info">
          <h2>${vehicle.vehicle_number} - ${vehicle.equipment_type || 'N/A'}</h2>
          <p><strong>Client:</strong> ${clientName} | <strong>Period:</strong> ${periodMonth}</p>
          ${vehicle.operator_name ? `<p><strong>Operator:</strong> ${vehicle.operator_name}</p>` : ''}
        </div>
        
        <div class="stats-grid">
          <div class="stat-card working">
            <div class="value">${stats.workingDays}</div>
            <div class="label">Working Days</div>
          </div>
          <div class="stat-card off">
            <div class="value">${stats.offDays}</div>
            <div class="label">Off Days</div>
          </div>
          <div class="stat-card total">
            <div class="value">${stats.totalHours}h</div>
            <div class="label">Total Hours</div>
          </div>
          <div class="stat-card avg">
            <div class="value">${stats.avgPerDay}h</div>
            <div class="label">Avg/Day</div>
          </div>
        </div>
        
        <table class="hours-table">
          <thead>
            <tr>
              ${Array.from({length: 31}, (_, i) => `<th>${i + 1}</th>`).join('')}
              <th class="total">Total</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              ${Array.from({length: 31}, (_, i) => {
                const dayNum = i + 1;
                const hours = dailyHours[dayNum] ?? dailyHours[String(dayNum)];
                const numHours = Number(hours);
                const isWorking = !isNaN(numHours) && numHours > 0;
                return `<td class="${isWorking ? 'working' : 'off'}">${isWorking ? hours : '-'}</td>`;
              }).join('')}
              <td class="total">${vehicle.total_hours}h</td>
            </tr>
          </tbody>
        </table>
        
        ${vehicle.total_overtime_hours > 0 ? `<p style="margin-bottom: 10px;"><strong>Overtime Hours:</strong> ${vehicle.total_overtime_hours}h</p>` : ''}
        ${vehicle.remarks ? `<p><strong>Remarks:</strong> ${vehicle.remarks}</p>` : ''}
        
        <div class="footer">
          Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()} | Nexus ERP
        </div>
      </body>
      </html>
    `;
    
    // Open print window
    const printWindow = window.open('', '_blank', 'width=900,height=700');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();
      
      // Trigger print after content loads
      printWindow.onload = () => {
        printWindow.print();
      };
      
      toast.success('Print dialog opened');
    } else {
      toast.error('Please allow pop-ups to print');
    }
  }, [normalizeDailyHours, computeVehicleStats, currentExtraction]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [companiesRes, suppliersRes, extractionsRes, timesheetsRes] = await Promise.all([
        getCompanies(),
        getSuppliers(),
        listOCRExtractions({ extraction_type: 'multi_vehicle_timesheet' }),
        listSupplierTimesheets({})
      ]);
      setCompanies(companiesRes.data);
      setSuppliers(suppliersRes.data || []);
      setExtractions(extractionsRes.data);
      setSupplierTimesheets(timesheetsRes.data);
      if (companiesRes.data.length > 0) {
        setSelectedCompanyId(companiesRes.data[0].id);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload JPEG, PNG, WEBP, or PDF.');
      return;
    }

    if (file.size > 20 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 20MB.');
      return;
    }

    setUploading(true);
    try {
      const response = await extractMultiVehicleTimesheet(file);
      toast.success(`Extracted ${response.data.vehicle_count} vehicles from ${response.data.client_name || 'document'}`);
      loadData();
      
      // Automatically open mapping dialog
      setCurrentExtraction(response.data);
      await loadVehiclesForMapping(response.data.extraction_id);
      setShowMappingDialog(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process document');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const loadVehiclesForMapping = async (extractionId) => {
    try {
      const response = await getExtractionVehicles(extractionId);
      setExtractionVehicles(response.data.vehicles || []);
      
      // Initialize supplier map from existing mappings
      const initialMap = {};
      for (const v of response.data.vehicles || []) {
        if (v.supplier_id) {
          initialMap[v.vehicle_number] = v.supplier_id;
        }
      }
      setVehicleSupplierMap(initialMap);
    } catch (error) {
      toast.error('Failed to load vehicles');
    }
  };

  const handleOpenMapping = async (extraction) => {
    setCurrentExtraction(extraction);
    await loadVehiclesForMapping(extraction.id);
    setShowMappingDialog(true);
  };

  const handleSupplierChange = (vehicleNumber, supplierId) => {
    setVehicleSupplierMap(prev => ({
      ...prev,
      [vehicleNumber]: supplierId
    }));
  };

  const handleConfirmAndCreate = async () => {
    if (!currentExtraction || !selectedCompanyId) {
      toast.error('Please select a company');
      return;
    }

    setProcessing(true);
    try {
      // First confirm the extraction if not already
      if (!currentExtraction.confirmed) {
        await confirmOCRExtraction(currentExtraction.extraction_id || currentExtraction.id);
      }

      // Save vehicle-supplier mappings
      for (const [vehicleNumber, supplierId] of Object.entries(vehicleSupplierMap)) {
        if (supplierId && supplierId !== 'own_fleet') {
          await mapVehicleToSupplier(vehicleNumber, supplierId);
        }
      }

      // Create supplier timesheets
      const mappingArray = Object.entries(vehicleSupplierMap)
        .filter(([_, sid]) => sid && sid !== 'own_fleet')
        .map(([vn, sid]) => ({ vehicle_number: vn, supplier_id: sid }));

      const response = await createSupplierTimesheets(
        currentExtraction.extraction_id || currentExtraction.id,
        selectedCompanyId,
        mappingArray
      );

      toast.success(`Created ${response.data.timesheets_created} supplier timesheet(s)`);
      setShowMappingDialog(false);
      setCurrentExtraction(null);
      setActiveTab('timesheets');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create supplier timesheets');
    } finally {
      setProcessing(false);
    }
  };

  const handleSendTimesheet = async (timesheetId) => {
    try {
      await sendSupplierTimesheet(timesheetId);
      toast.success('Timesheet sent to supplier');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send timesheet');
    }
  };

  return (
    <div className="space-y-6" data-testid="supplier-timesheets-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black tracking-tight flex items-center gap-2">
            <Truck className="h-8 w-8 text-blue-600" />
            Supplier Timesheets
          </h1>
          <p className="text-muted-foreground">
            Extract vehicle timesheets from client documents and create supplier timesheets
          </p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="upload" data-testid="tab-upload">
            <Upload className="h-4 w-4 mr-2" />
            Upload & Extract
          </TabsTrigger>
          <TabsTrigger value="extractions" data-testid="tab-extractions">
            <Sparkles className="h-4 w-4 mr-2" />
            Extractions ({extractions.length})
          </TabsTrigger>
          <TabsTrigger value="timesheets" data-testid="tab-timesheets">
            <FileText className="h-4 w-4 mr-2" />
            Supplier Timesheets ({supplierTimesheets.length})
          </TabsTrigger>
        </TabsList>

        {/* Upload Tab */}
        <TabsContent value="upload" className="space-y-4">
          <Card className="border-2 border-dashed">
            <CardContent className="pt-6">
              <div className="text-center">
                <Upload className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="font-semibold mb-2">Upload Client Timesheet</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Upload a client timesheet PDF containing multiple vehicles.
                  AI will extract each vehicle&apos;s hours separately for supplier mapping.
                </p>
                <div className="flex justify-center">
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp,application/pdf"
                      className="hidden"
                      onChange={handleFileUpload}
                      disabled={uploading}
                      data-testid="timesheet-file-input"
                    />
                    <Button disabled={uploading} asChild>
                      <span>
                        {uploading ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Extracting Vehicles...
                          </>
                        ) : (
                          <>
                            <Upload className="h-4 w-4 mr-2" />
                            Select Timesheet PDF
                          </>
                        )}
                      </span>
                    </Button>
                  </label>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Supported: JPEG, PNG, WEBP, PDF (max 20MB)
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Quick Stats */}
          <div className="grid grid-cols-3 gap-4">
            <Card>
              <CardContent className="pt-4">
                <div className="text-center">
                  <p className="text-3xl font-bold text-blue-600">{extractions.length}</p>
                  <p className="text-sm text-muted-foreground">Extractions</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-center">
                  <p className="text-3xl font-bold text-green-600">
                    {extractions.filter(e => e.confirmed).length}
                  </p>
                  <p className="text-sm text-muted-foreground">Confirmed</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-center">
                  <p className="text-3xl font-bold text-purple-600">{supplierTimesheets.length}</p>
                  <p className="text-sm text-muted-foreground">Supplier Timesheets</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Extractions Tab */}
        <TabsContent value="extractions" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Multi-Vehicle Extractions</CardTitle>
              <CardDescription>Client timesheets with multiple vehicles extracted</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
                </div>
              ) : extractions.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No extractions yet. Upload a timesheet to get started.
                </div>
              ) : (
                <div className="space-y-3">
                  {extractions.map(ext => (
                    <div 
                      key={ext.id} 
                      className="flex items-center justify-between p-4 bg-muted/50 rounded-lg hover:bg-muted"
                      data-testid={`extraction-${ext.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <FileText className="h-8 w-8 text-blue-600" />
                        <div>
                          <p className="font-semibold">{ext.client_name || ext.file_name}</p>
                          <div className="flex items-center gap-3 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Truck className="h-3 w-3" />
                              {ext.vehicle_count} vehicles
                            </span>
                            <span>{new Date(ext.created_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {ext.supplier_timesheets_created ? (
                          <Badge className="bg-green-100 text-green-800">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Processed
                          </Badge>
                        ) : ext.confirmed ? (
                          <Badge className="bg-blue-100 text-blue-800">Confirmed</Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-800">Pending</Badge>
                        )}
                        {!ext.supplier_timesheets_created && (
                          <Button 
                            size="sm" 
                            onClick={() => handleOpenMapping(ext)}
                            data-testid={`map-btn-${ext.id}`}
                          >
                            <Users className="h-4 w-4 mr-1" />
                            Map Suppliers
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Supplier Timesheets Tab */}
        <TabsContent value="timesheets" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Supplier Timesheets</CardTitle>
              <CardDescription>Individual timesheets for each supplier</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
                </div>
              ) : supplierTimesheets.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No supplier timesheets yet. Process an extraction to create them.
                </div>
              ) : (
                <div className="space-y-3">
                  {supplierTimesheets.map(ts => (
                    <div 
                      key={ts.id} 
                      className={`flex items-center justify-between p-4 rounded-lg ${
                        ts.is_own_fleet ? 'bg-blue-50' : 'bg-muted/50'
                      }`}
                      data-testid={`timesheet-${ts.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`p-2 rounded-full ${ts.is_own_fleet ? 'bg-blue-100' : 'bg-purple-100'}`}>
                          {ts.is_own_fleet ? (
                            <Building className="h-5 w-5 text-blue-600" />
                          ) : (
                            <Users className="h-5 w-5 text-purple-600" />
                          )}
                        </div>
                        <div>
                          <p className="font-semibold">{ts.supplier_name}</p>
                          <div className="flex items-center gap-3 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Truck className="h-3 w-3" />
                              {ts.vehicle_count} vehicles
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {ts.total_hours} hours
                            </span>
                            <span>{ts.month}</span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            Client: {ts.client_name}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={
                          ts.status === 'sent' ? 'bg-green-100 text-green-800' :
                          ts.status === 'confirmed' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'
                        }>
                          {ts.status}
                        </Badge>
                        {!ts.is_own_fleet && ts.status === 'draft' && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleSendTimesheet(ts.id)}
                            data-testid={`send-btn-${ts.id}`}
                          >
                            <Send className="h-4 w-4 mr-1" />
                            Send
                          </Button>
                        )}
                        <Button size="sm" variant="ghost">
                          <Eye className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Vehicle-Supplier Mapping Dialog */}
      <Dialog open={showMappingDialog} onOpenChange={setShowMappingDialog}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-purple-600" />
              Map Vehicles to Suppliers
            </DialogTitle>
          </DialogHeader>
          
          {currentExtraction && (
            <div className="space-y-4">
              {/* Extraction Info */}
              <div className="p-3 bg-muted rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Building className="h-4 w-4" />
                  <span className="font-medium">{currentExtraction.client_name || currentExtraction.extracted_data?.client_name}</span>
                </div>
                <div className="flex gap-4 text-sm text-muted-foreground">
                  <span>{extractionVehicles.length} vehicles</span>
                  <span>{currentExtraction.extracted_data?.period_month || 'N/A'}</span>
                </div>
              </div>

              {/* Company Selection */}
              <div>
                <Label>Company *</Label>
                <Select value={selectedCompanyId} onValueChange={setSelectedCompanyId}>
                  <SelectTrigger data-testid="select-company">
                    <SelectValue placeholder="Select company" />
                  </SelectTrigger>
                  <SelectContent>
                    {companies.map(company => (
                      <SelectItem key={company.id} value={company.id}>
                        {company.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Vehicle Mapping Table with Day-wise Hours */}
              <div className="border rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium sticky left-0 bg-muted z-10">Vehicle</th>
                        <th className="px-3 py-2 text-left font-medium">Type</th>
                        {/* Day columns 1-31 */}
                        {Array.from({length: 31}, (_, i) => (
                          <th key={i} className="px-1 py-2 text-center font-medium min-w-[32px] text-xs text-gray-500">
                            {i + 1}
                          </th>
                        ))}
                        <th className="px-3 py-2 text-right font-medium bg-blue-50">Total</th>
                        <th className="px-3 py-2 text-left font-medium">Supplier</th>
                        <th className="px-2 py-2 text-center font-medium">View</th>
                      </tr>
                    </thead>
                    <tbody>
                      {extractionVehicles.map((vehicle, idx) => {
                        const dailyHours = normalizeDailyHours(vehicle.daily_hours);
                        return (
                          <tr key={idx} className="border-t hover:bg-muted/30">
                            <td className="px-3 py-2 sticky left-0 bg-white z-10">
                              <div>
                                <p className="font-medium text-xs">{vehicle.vehicle_number}</p>
                                {vehicle.operator_name && (
                                  <p className="text-xs text-muted-foreground">{vehicle.operator_name}</p>
                                )}
                              </div>
                            </td>
                            <td className="px-3 py-2 text-xs">{vehicle.equipment_type}</td>
                            {/* Day-wise hours columns */}
                            {Array.from({length: 31}, (_, i) => {
                              const dayNum = i + 1;
                              const hours = dailyHours[dayNum] ?? dailyHours[String(dayNum)] ?? '-';
                              const numHours = Number(hours);
                              const isWorking = !isNaN(numHours) && numHours > 0;
                              const isOff = hours === '-' || hours === 'X' || hours === 'F' || hours === 'OFF' || hours === 0 || hours === '0' || hours === undefined;
                              return (
                                <td 
                                  key={i} 
                                  className={`px-1 py-2 text-center text-xs ${
                                    isWorking ? 'bg-green-50 text-green-700 font-medium' : 
                                    isOff ? 'bg-red-50 text-red-400' : 'bg-gray-50 text-gray-400'
                                  }`}
                                >
                                  {isWorking ? hours : '-'}
                                </td>
                              );
                            })}
                            <td className="px-3 py-2 text-right font-bold bg-blue-50">{vehicle.total_hours}h</td>
                            <td className="px-3 py-2">
                              <Select 
                                value={vehicleSupplierMap[vehicle.vehicle_number] || 'own_fleet'}
                                onValueChange={(val) => handleSupplierChange(vehicle.vehicle_number, val)}
                              >
                                <SelectTrigger className="w-32 h-8 text-xs" data-testid={`supplier-select-${idx}`}>
                                  <SelectValue placeholder="Select" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="own_fleet">
                                    <span className="flex items-center gap-1">
                                      <Building className="h-3 w-3" />
                                      Own Fleet
                                    </span>
                                  </SelectItem>
                                  {suppliers.map(sup => (
                                    <SelectItem key={sup.id} value={sup.id}>
                                      {sup.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </td>
                            <td className="px-2 py-2 text-center">
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 w-7 p-0"
                                onClick={() => handleViewVehicleDetails(vehicle)}
                                data-testid={`view-vehicle-${idx}`}
                              >
                                <Eye className="h-4 w-4 text-blue-600" />
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Summary */}
              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="text-sm">
                  <span className="font-medium">Summary: </span>
                  {Object.values(vehicleSupplierMap).filter(v => v && v !== 'own_fleet').length} vehicles mapped to suppliers,
                  {' '}{extractionVehicles.length - Object.values(vehicleSupplierMap).filter(v => v && v !== 'own_fleet').length} as own fleet
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMappingDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmAndCreate} 
              disabled={processing || !selectedCompanyId}
              data-testid="create-timesheets-btn"
            >
              {processing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Create Supplier Timesheets
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Vehicle Details Dialog - Table Grid Layout */}
      <Dialog open={showVehicleDialog} onOpenChange={setShowVehicleDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-blue-600" />
              Day-Wise Hours Breakdown
            </DialogTitle>
          </DialogHeader>

          {selectedVehicle && (
            <div className="space-y-4">
              {/* Vehicle Info Header */}
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
                    <Truck className="h-6 w-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="font-bold text-lg">{selectedVehicle.vehicle_number} - {selectedVehicle.equipment_type}</p>
                    <p className="text-sm text-muted-foreground">
                      {selectedVehicle.operator_name && `Operator: ${selectedVehicle.operator_name} • `}
                      Total: <span className="font-semibold text-blue-600">{selectedVehicle.total_hours} hours</span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Day-wise Hours Table Grid */}
              <div className="border rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        {Array.from({length: 31}, (_, i) => (
                          <th key={i} className="px-2 py-2 text-center font-semibold text-xs text-gray-600 min-w-[38px]">
                            {i + 1}
                          </th>
                        ))}
                        <th className="px-3 py-2 text-center font-bold text-xs bg-blue-100 text-blue-800 min-w-[50px]">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        {Array.from({length: 31}, (_, i) => {
                          const dayNum = i + 1;
                          const dailyHours = normalizeDailyHours(selectedVehicle.daily_hours);
                          const hours = dailyHours[dayNum] ?? dailyHours[String(dayNum)];
                          const numHours = Number(hours);
                          const isWorking = !isNaN(numHours) && numHours > 0;
                          
                          return (
                            <td 
                              key={i} 
                              className={`px-2 py-3 text-center text-sm font-medium ${
                                isWorking ? 'bg-green-50 text-green-700' : 
                                'bg-red-50 text-red-400'
                              }`}
                            >
                              {isWorking ? hours : '-'}
                            </td>
                          );
                        })}
                        <td className="px-3 py-3 text-center font-bold bg-blue-100 text-blue-800">
                          {selectedVehicle.total_hours}h
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Summary Stats */}
              {(() => {
                const stats = computeVehicleStats(selectedVehicle);
                return (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="bg-green-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-green-600">{stats.workingDays}</p>
                      <p className="text-xs text-gray-500">Working Days</p>
                    </div>
                    <div className="bg-red-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-red-500">{stats.offDays}</p>
                      <p className="text-xs text-gray-500">Off Days</p>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-blue-600">{stats.totalHours}h</p>
                      <p className="text-xs text-gray-500">Total Hours</p>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-purple-600">{stats.avgPerDay}h</p>
                      <p className="text-xs text-gray-500">Avg/Day</p>
                    </div>
                  </div>
                );
              })()}

              {/* Additional Info */}
              {(selectedVehicle.remarks || selectedVehicle.total_overtime_hours > 0) && (
                <div className="p-3 bg-gray-50 rounded-lg space-y-2">
                  {selectedVehicle.total_overtime_hours > 0 && (
                    <p className="text-sm">
                      <span className="font-medium">Overtime Hours:</span>{' '}
                      <span className="text-orange-600 font-semibold">{selectedVehicle.total_overtime_hours}h</span>
                    </p>
                  )}
                  {selectedVehicle.remarks && (
                    <p className="text-sm">
                      <span className="font-medium">Remarks:</span> {selectedVehicle.remarks}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          <DialogFooter className="flex justify-between sm:justify-between">
            <Button 
              variant="outline" 
              onClick={() => handlePrintVehicleTimesheet(selectedVehicle)}
              className="flex items-center gap-2"
              data-testid="print-vehicle-btn"
            >
              <Printer className="h-4 w-4" />
              Print / Export PDF
            </Button>
            <Button variant="outline" onClick={() => setShowVehicleDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SupplierTimesheetsPage;
